import { useRef, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { Download, ShieldAlert } from 'lucide-react';
import clsx from 'clsx';
import type { ExportFormat } from '@idas/shared';
import { api, ApiError, downloadBlob } from '../lib/api';
import { Modal } from './ui/Modal';
import { ReasonField } from './ui/ReasonField';

interface Props {
  queryRunId: string;
  rowCount: number;
  onClose: () => void;
}

/** The server requires this too; keeping them equal avoids a confusing 400. */
const MIN_REASON = 15;

/**
 * One-click justifications.
 *
 * The reason is mandatory because an export is the moment data leaves the
 * controlled surface, and it is the first thing an investigator reads. But
 * making someone compose a sentence for a routine download is how required
 * fields end up full of "asdf" — presets give a real answer instantly and stay
 * editable for anything else.
 */
const REASON_PRESETS = [
  'Quarterly reporting pack for the leadership review.',
  'Reconciling figures against the finance team’s records.',
  'Requested by my line manager for the board pack.',
  'Data quality check against the source system.',
];

/**
 * Exporting is the one action that asks the analyst to justify themselves.
 *
 * Running a query keeps data inside a controlled, audited surface. Exporting
 * puts a file on a laptop, and that is the moment the organisation loses
 * control of it. The friction here is deliberate and proportionate.
 */
export function ExportDialog({ queryRunId, rowCount, onClose }: Props) {
  const [format, setFormat] = useState<ExportFormat>('CSV');
  const [reason, setReason] = useState('');
  const [attempted, setAttempted] = useState(false);
  const reasonRef = useRef<HTMLTextAreaElement>(null);

  const exportMutation = useMutation({
    mutationFn: () => api.query.export({ queryRunId, format, reason }),
    onSuccess: (payload) => {
      downloadBlob(payload.blob, payload.filename);
      onClose();
    },
  });

  const tooShort = reason.trim().length < MIN_REASON;

  /**
   * Clickable even when the reason is missing.
   *
   * A disabled button explains nothing — you press it, nothing happens, and
   * there is no way to discover why. Letting the click through and pointing at
   * the field turns a dead end into an instruction.
   */
  const attemptDownload = () => {
    if (!tooShort) {
      exportMutation.mutate();
      return;
    }
    setAttempted(true);
    reasonRef.current?.focus();
    reasonRef.current?.scrollIntoView({ block: 'center', behavior: 'smooth' });
  };

  const error = exportMutation.error;

  return (
    <Modal
      title="Export results"
      description="The query is re-run and re-checked against current policy, so the file reflects what you are allowed to see right now."
      onClose={onClose}
      footer={
        <>
          {attempted && tooShort && (
            <span className="mr-auto max-w-[20rem] text-2xs leading-snug text-warn">
              Add a reason of at least {MIN_REASON} characters ({reason.trim().length} so far).
            </span>
          )}
          <span
            className={clsx(
              'text-2xs tabular-nums text-ink-faint',
              attempted && tooShort && 'hidden sm:inline',
            )}
          >
            up to {rowCount.toLocaleString()} rows
          </span>
          <button className="btn" onClick={onClose}>
            Cancel
          </button>
          <button
            className="btn btn-primary"
            disabled={exportMutation.isPending}
            onClick={attemptDownload}
          >
            <Download className="h-3.5 w-3.5" />
            {exportMutation.isPending ? 'Preparing…' : 'Download'}
          </button>
        </>
      }
    >
      <label className="mb-1.5 block text-2xs font-medium text-ink-muted">Format</label>
      <div className="mb-4 flex gap-1.5">
        {(['CSV', 'JSON'] as const).map((option) => (
          <button
            key={option}
            className={clsx('btn flex-1', format === option && 'btn-primary')}
            onClick={() => setFormat(option)}
          >
            {option}
          </button>
        ))}
      </div>

      <ReasonField
        ref={reasonRef}
        id="export-reason"
        label="Why do you need this data outside the console?"
        value={reason}
        onChange={setReason}
        minLength={MIN_REASON}
        presets={REASON_PRESETS}
        placeholder="e.g. Quarterly churn analysis for the Q3 board pack, requested by Finance."
        showError={attempted}
        hint="Recorded against your name, along with a fingerprint of the file."
      />

      <p className="mt-1.5 flex items-start gap-1.5 text-2xs leading-relaxed text-ink-faint">
        <ShieldAlert className="mt-px h-3 w-3 shrink-0" />
        <span>Exported data remains your responsibility.</span>
      </p>

      {error && (
        <p className="mt-3 rounded-md border border-danger/30 bg-danger/10 p-2.5 text-2xs leading-relaxed text-danger">
          {error instanceof ApiError ? error.message : 'The export failed.'}
        </p>
      )}
    </Modal>
  );
}
